// Vendors
import React, { useState, useEffect } from 'react';
import axios from 'axios';

const DocumentEpat = ({
  projectData,
  handleInputChange,
  handleChangeView,
  handleSaveProject,
  updateSpecificProjectDataProperty
}) => {
  const [file, setFile] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [ajaxResponse, setAjaxResponse] = useState({ type: '', message: '' });
  const [submittedFile, setSubmittedFile] = useState(projectData.document_epat);
  const [submittedFileLoading, setSubmittedFileLoading] = useState(true);
  const [docID, setDocID] = useState(
    projectData.document_epat ? projectData.document_epat : 0
  );
  const epatTableFields = {
    so2: 'SO2 (lb)',
    nox: 'NOx (lb)',
    co2: 'CO2 (1000 lb)',
    ch4: 'CH4 (lb)',
    n2o: 'N2O (lb)',
    co2e: 'CO2e (1000 lb)'
  };
  const { pid } = projectData;
  let _isSubscribed = true;

  const allEpatEmissionsFilledOut = () => {
    let errors = 0;

    Object.entries(epatTableFields).map((field) =>
      Array.from(['alternative', 'baseline']).map((property) => {
        const propertyName = `epat_emissions_${property}_${field[0]}`;

        if (
          projectData[propertyName] === '' ||
          projectData[propertyName] == '0'
        )
          errors++;
      })
    );

    return errors === 0;
  };

  const handleFileSelect = (event) => {
    if (event.target.files[0].type !== 'application/pdf') {
      setError('Please upload a PDF document.');
      setFile(undefined);
    } else {
      setError('');
      setFile(event.target.files[0]);
    }
  };

  const handleRemoveFile = () => {
    const confirmation = confirm(
      'Are you sure you want to delete this EPAT document?'
    );

    if (!confirmation) return;

    const formData = new FormData();
    formData.append('action', 'wl_pip_delete_document');
    formData.append('doctype', 'epat');
    formData.append('pid', pid);

    axios({
      method: 'post',
      url: window.wlPipConfig.ajaxUrl,
      data: formData
    })
      .then(({ data: { success, data } }) => {
        if (success) {
          setFile(undefined);
          setError('');
          setSubmittedFile({});
          setSubmittedFileLoading(false);
          setDocID(0);
          updateSpecificProjectDataProperty('document_epat', 0);
          setAjaxResponse({
            type: success ? 'success' : 'error',
            message: data.message
          });
        }
      })
      .catch(({ data: { success, data } }) => {
        setSubmittedFileLoading(false);
        setAjaxResponse({
          type: success ? 'success' : 'error',
          message: data
        });
      });
  };

  const getDocumentFileName = () => {
    if (!docID) return;

    _isSubscribed = true;

    setSubmittedFileLoading(true);

    const formData = new FormData();
    formData.append('action', 'wl_pip_get_document_by_id');
    formData.append('doc_id', docID);

    axios({
      method: 'post',
      url: window.wlPipConfig.ajaxUrl,
      data: formData
    })
      .then(({ data: { success, data } }) => {
        // to prevent memory leak (https://juliangaramendy.dev/blog/use-promise-subscription)
        if (_isSubscribed) {
          if (success) {
            setSubmittedFile(data);
          } else {
            setDocID(0);
          }

          setSubmittedFileLoading(false);
        }
      })
      .catch(({ data: { success, data } }) => {
        setSubmittedFileLoading(false);
        setAjaxResponse({
          type: success ? 'success' : 'error',
          message: data
        });
      });

    return;
  };

  // Run this whenever the value of 'file' changes. (uploads document to server)
  useEffect(() => {
    if (!file) return;

    setLoading(true);

    const formData = new FormData();
    formData.append('action', 'wl_pip_doc_upload');
    formData.append('pid', pid);
    formData.append('doc_type', 'epat');
    formData.append('file', file, file.name);

    axios({
      method: 'post',
      url: window.wlPipConfig.ajaxUrl,
      data: formData,
      headers: { 'Content-Type': 'multipart/form-data' }
    })
      .then(({ data: { success, data } }) => {
        setLoading(false);
        setAjaxResponse({
          type: success ? 'success' : 'error',
          message: data.message
        });

        if (data.doc_id) {
          setDocID(data.doc_id);
          updateSpecificProjectDataProperty('document_epat', data.doc_id);
        }
      })
      .catch(({ data: { success, data } }) => {
        setLoading(false);
        setAjaxResponse({
          type: success ? 'success' : 'error',
          message: data.message
        });
      });
  }, [file]);

  // Run this whenever the value of docID changes. (updates "Current EPAT on file: {..}")
  useEffect(() => {
    _isSubscribed = true;
    if (docID) getDocumentFileName();

    return () => (_isSubscribed = false);
  }, [docID]);

  return (
    <div id="upload-epat">
      <div className="grid u--font-size-lg">
        <div className="grid__col-xs-12">
          <table className="epat__table">
            <thead>
              <tr>
                <th></th>
                {Object.entries(epatTableFields).map((field, index) => (
                  <th key={index}>{field[1]}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Baseline</td>
                {Object.entries(epatTableFields).map((field, index) => {
                  const propertyName = `epat_emissions_baseline_${field[0]}`;
                  return (
                    <td key={index}>
                      <input
                        type="number"
                        className={`form__input form__input--sm form__input--no-shadow${
                          parseFloat(projectData[propertyName]) <= 0
                            ? ' form__input--error'
                            : ''
                        }`}
                        name={`epat_emissions_baseline_${field[0]}`}
                        value={projectData[propertyName]}
                        onChange={(event) => handleInputChange(event)}
                      />
                    </td>
                  );
                })}
              </tr>
              <tr>
                <td>Alternative</td>
                {Object.entries(epatTableFields).map((field, index) => {
                  const propertyName = `epat_emissions_alternative_${field[0]}`;

                  return (
                    <td key={index}>
                      <input
                        type="number"
                        className={`form__input form__input--sm form__input--no-shadow${
                          parseFloat(projectData[propertyName]) <= 0
                            ? ' form__input--error'
                            : ''
                        }`}
                        name={`epat_emissions_alternative_${field[0]}`}
                        value={projectData[propertyName]}
                        onChange={(event) => handleInputChange(event)}
                      />
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
        <div className="grid__col-xs-12">
          {docID !== 0 && (
            <p
              className={`u--margin-bottom-10 u--font-size-md${
                submittedFileLoading ? ' is-loading' : ''
              }`}
            >
              Current EPAT on file:{' '}
              {submittedFile && (
                <>
                  <a
                    href={submittedFile.url}
                    title={submittedFile.filename}
                    target="_blank"
                  >
                    {submittedFile.filename}
                  </a>
                  <span
                    className="document__trash perc-icon-trash"
                    onClick={() => handleRemoveFile()}
                  />
                </>
              )}
              <br />
              <span>Uploaded on {submittedFile && submittedFile.date}</span>
            </p>
          )}
          <div
            className="form__field form__field--required"
            style={{ maxWidth: '600px' }}
          >
            <small className={`form__help ${!error ? 'u--display-none' : ''}`}>
              <span className="form__help-icon icon icon--md perc-icon-caret-right"></span>
              <div className="form__help-message">{error && error}</div>
            </small>
            <label className="custom-file-upload">
              <input
                type="file"
                className="u--display-none" // breaks functionality on IE8 and Android 4, who cares
                onChange={(event) => handleFileSelect(event)}
              />
              <div
                className={`btn btn--upload btn--link btn--lg btn--block btn--upload ${
                  file !== undefined ? 'btn--upload--ready' : ''
                } ${
                  file !== undefined && file['name'].length > 40
                    ? 'btn--md'
                    : 'btn--lg'
                }`}
              >
                <span className="btn__content">
                  <span className="btn__text">
                    {file !== undefined ? file.name : 'Select File'}
                  </span>
                </span>
              </div>
            </label>
          </div>
          {ajaxResponse.type && ajaxResponse.message && (
            <div
              className={`notice notice--${ajaxResponse.type}`}
              role="alert"
              style={{ maxWidth: '600px' }}
            >
              {ajaxResponse.message}
            </div>
          )}
        </div>
        <div className="grid__col-xs-12">
          <button
            id="pip-add__save-epat"
            className={`btn btn--gold btn--lg module-hover-root ${
              loading ? 'btn--loading' : ''
            }`}
            type="button"
            onClick={() => handleSaveProject()}
            disabled={loading || !allEpatEmissionsFilledOut()}
          >
            <span className="btn__content">
              <span className="btn__text">Save EPAT</span>
            </span>
          </button>
          <span
            className="btn btn--white btn--lg module-hover-root u--margin-left-30"
            onClick={() => handleChangeView('project-form')}
          >
            <span className="btn__content">
              <span className="btn__text">Back to Project Overview</span>
            </span>
          </span>
        </div>
      </div>
    </div>
  );
};
export default DocumentEpat;
